import { useTranslation } from "react-i18next";
import { Plus, X, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface RsvpQuestion {
  id: string;
  question: string;
  type: "single" | "multiple" | "text";
  options: string[];
}

interface Props {
  questions: RsvpQuestion[];
  onChange: (questions: RsvpQuestion[]) => void;
}

const RsvpQuestionBuilder = ({ questions, onChange }: Props) => {
  const { t } = useTranslation();

  const addQuestion = () => {
    onChange([
      ...questions,
      {
        id: crypto.randomUUID(),
        question: "",
        type: "single",
        options: ["", ""],
      },
    ]);
  };

  const updateQuestion = (index: number, updates: Partial<RsvpQuestion>) => {
    const updated = questions.map((q, i) => (i === index ? { ...q, ...updates } : q));
    onChange(updated);
  };

  const removeQuestion = (index: number) => {
    onChange(questions.filter((_, i) => i !== index));
  };

  const addOption = (qIndex: number) => {
    const q = questions[qIndex];
    updateQuestion(qIndex, { options: [...q.options, ""] });
  };

  const updateOption = (qIndex: number, oIndex: number, value: string) => {
    const q = questions[qIndex];
    const options = q.options.map((o, i) => (i === oIndex ? value : o));
    updateQuestion(qIndex, { options });
  };

  const removeOption = (qIndex: number, oIndex: number) => {
    const q = questions[qIndex];
    if (q.options.length <= 2) return;
    updateQuestion(qIndex, { options: q.options.filter((_, i) => i !== oIndex) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-muted-foreground">
          {t("settings.rsvpQuestions", { defaultValue: "Guest Preferences" })}
        </Label>
      </div>

      {questions.map((q, qIndex) => (
        <div key={q.id} className="border border-border rounded-xl p-3 space-y-3 bg-muted/30">
          <div className="flex items-start gap-2">
            <GripVertical size={14} className="text-muted-foreground mt-2.5 shrink-0" />
            <div className="flex-1 space-y-2">
              <Input
                placeholder={t("settings.questionPlaceholder", { defaultValue: "e.g. What food do you prefer?" })}
                value={q.question}
                onChange={(e) => updateQuestion(qIndex, { question: e.target.value })}
                className="h-9 text-sm"
              />
              <div className="flex gap-1.5">
                {(["single", "multiple", "text"] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => updateQuestion(qIndex, { type })}
                    className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                      q.type === type
                        ? "border-primary bg-primary/10 text-primary font-medium"
                        : "border-border text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    {type === "single"
                      ? t("settings.singleChoice", { defaultValue: "Single choice" })
                      : type === "multiple"
                      ? t("settings.multipleChoice", { defaultValue: "Multiple choice" })
                      : t("settings.freeText", { defaultValue: "Free text" })}
                  </button>
                ))}
              </div>
              {q.type !== "text" && (
                <div className="space-y-1.5">
                  {q.options.map((opt, oIndex) => (
                    <div key={oIndex} className="flex items-center gap-1.5">
                      <div className={`w-3.5 h-3.5 shrink-0 border border-muted-foreground/40 ${q.type === "single" ? "rounded-full" : "rounded-sm"}`} />
                      <Input
                        placeholder={t("settings.optionPlaceholder", { defaultValue: `Option ${oIndex + 1}` })}
                        value={opt}
                        onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                        className="h-7 text-xs flex-1"
                      />
                      {q.options.length > 2 && (
                        <button onClick={() => removeOption(qIndex, oIndex)} className="text-muted-foreground hover:text-destructive p-0.5">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => addOption(qIndex)}
                    className="text-[10px] text-primary hover:underline flex items-center gap-1 mt-1"
                  >
                    <Plus size={10} /> {t("settings.addOption", { defaultValue: "Add option" })}
                  </button>
                </div>
              )}
            </div>
            <button onClick={() => removeQuestion(qIndex)} className="text-muted-foreground hover:text-destructive p-1 shrink-0">
              <X size={14} />
            </button>
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addQuestion}
        className="w-full rounded-full h-9 text-xs gap-1.5"
      >
        <Plus size={14} />
        {t("settings.addQuestion", { defaultValue: "Add a preference question" })}
      </Button>
    </div>
  );
};

export default RsvpQuestionBuilder;
